import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import type { StepInfo } from "./BookingStepHeader";

interface MobileStepNavProps {
  readonly currentStepInfo: StepInfo | undefined;
  readonly currentStepNumber: number;
  readonly totalStepCount: number;
  /**
   * Optional callback for the back button. When provided, overrides the default
   * `window.history.back()` behavior — useful for flows where steps are tracked
   * via React state rather than URL history (e.g. PlaceYourOrder).
   */
  readonly onBack?: () => void;
}

/**
 * MobileStepNav — step indicator shown inside the BookingHeader on small screens.
 *
 * Displays a back button, current step label, and "Step N of M" counter.
 * Extracted from BookingForm to reduce cognitive complexity.
 */
const MobileStepNav: React.FC<MobileStepNavProps> = ({
  currentStepInfo,
  currentStepNumber,
  totalStepCount,
  onBack,
}) => {
  const { t } = useTranslation();

  return (
    <div
      id="gc-booking-step-navigation-mobile"
      className="gcw:flex gcw:items-center gcw:justify-between gcw:w-full gcw:lg:hidden gcw:mt-4"
    >
      <button
        type="button"
        className="back-button-transparent"
        onClick={onBack ?? (() => window.history.back())}
        aria-label={t("back", "Back")}
      >
        <ChevronLeft size={20} color="white" />
      </button>
      {currentStepInfo && (
        <div className="gcw:flex gcw:items-center gcw:gap-2 gcw:text-white">
          <span className="gcw:text-body gcw:font-normal">
            {t(`booking_step_${currentStepInfo.stage}`, currentStepInfo.name)}
          </span>
          <span aria-hidden="true">{"\u00B7"}</span>
          <span className="gcw:text-body gcw:font-normal">
            {t("booking_step_of", "Step {{current}} of {{total}}", {
              current: currentStepNumber,
              total: totalStepCount,
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export default MobileStepNav;
